import * as React from 'react';
import { useState, useMemo, useCallback, useEffect } from 'react';

import { find, includes, head } from 'lodash';

import { titleize } from '@admin/components/helpers/titleize';
import { Assignment__Role } from '@admin/schema';

/**
 * User type for dispatcher assignments
 */
interface User {
  id: string | number;
  name: string;
  roles: string[];
  region_ids: number[];
  driver_approved?: boolean;
}

/**
 * Assignment type for dispatch
 */
interface Assignment {
  id?: string | number | null;
  role?: string;
  user?: User;
  _destroy?: boolean;
}

/**
 * Field shift assignment type
 */
interface FieldShiftAssignment {
  user_id: string | number;
  dispatch_ids: (string | number)[];
}

/**
 * Dispatch type
 */
interface Dispatch {
  id?: string | number;
  assignments: Assignment[];
}

/**
 * Filters type
 */
interface Filters {
  region_id?: number;
}

/**
 * Section type for grouping users
 */
interface Section {
  name: string;
  style: 'full' | 'warning' | 'none' | 'default';
  users: User[];
}

/**
 * Props for DispatcherAssignmentsManager component
 * @param dispatch - The dispatch object containing assignments
 * @param users - List of all users
 * @param fieldShiftAssignments - List of field shift assignments
 * @param filters - Current filters including region_id
 * @param editing - Whether the component is in editing mode
 * @param onDispatchUpdated - Callback when dispatch is updated
 */
interface DispatcherAssignmentsManagerProps {
  dispatch: Dispatch;
  users: User[];
  fieldShiftAssignments: FieldShiftAssignment[];
  filters: Filters;
  editing: boolean;
  onDispatchUpdated: (dispatch: Dispatch) => void;
}

/**
 * Escape special regex characters in a string to prevent regex injection
 * @param str - The string to escape
 * @returns The escaped string safe for use in RegExp
 */
const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Get initials from a name string
 * @param name - The full name
 * @returns The initials
 */
const getInitials = (name?: string): string => {
  if (!name) return '';
  return name
    .split(/\W+/)
    .map((component) => component.charAt(0))
    .join('');
};

/**
 * Highlight matching text within a name
 * @param name - The user name
 * @param pattern - The search pattern to highlight
 * @returns Object with parts array for rendering
 */
interface HighlightPart {
  text: string;
  highlight: boolean;
}

const getHighlightedParts = (name: string, pattern?: string | null): HighlightPart[] => {
  if (!pattern || pattern.length === 0) {
    return [{ text: name, highlight: false }];
  }

  const escapedPattern = escapeRegExp(pattern);
  const regex = new RegExp(`(${escapedPattern})`, 'gi');
  const parts: HighlightPart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(name)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: name.slice(lastIndex, match.index), highlight: false });
    }
    parts.push({ text: match[1], highlight: true });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < name.length) {
    parts.push({ text: name.slice(lastIndex), highlight: false });
  }

  return parts.length > 0 ? parts : [{ text: name, highlight: false }];
};

/**
 * DispatcherAssignmentsManager component
 * Manages mover assignments for a dispatch route
 */
export const DispatcherAssignmentsManager: React.FC<DispatcherAssignmentsManagerProps> = ({
  dispatch,
  users,
  fieldShiftAssignments,
  filters,
  editing,
  onDispatchUpdated,
}) => {
  const [selection, setSelection] = useState<Assignment | null>(null);
  const [autocomplete, setAutocomplete] = useState<string | null>(null);
  const [searching, setSearching] = useState<User[] | null>(null);
  const [activeAssignment, setActiveAssignment] = useState<Assignment | null>(null);

  /**
   * Check if user is a mover
   * @param user - The user to check
   * @returns True if user has mover role
   */
  const isMover = useCallback((user: User): boolean => {
    return includes(user.roles, 'mover');
  }, []);

  /**
   * Check if user is selectable (mover in current region)
   * @param user - The user to check
   * @returns True if user is a mover in the current region
   */
  const isSelectable = useCallback(
    (user: User): boolean => {
      return isMover(user) && includes(user.region_ids, filters.region_id);
    },
    [filters.region_id, isMover],
  );

  /**
   * Get movers who are assigned to shifts and dispatched
   */
  const moversAssignedDispatches = useMemo((): User[] => {
    const userList = searching ?? users;
    return userList.filter(
      (user) =>
        isSelectable(user) &&
        fieldShiftAssignments
          .filter((assignment) => assignment.dispatch_ids.length > 0)
          .some((assignment) => assignment.user_id === user.id),
    );
  }, [searching, users, isSelectable, fieldShiftAssignments]);

  /**
   * Get movers who are assigned to shifts but not dispatched
   */
  const moversAssignedShifts = useMemo((): User[] => {
    const userList = searching ?? users;
    return userList.filter(
      (user) =>
        isSelectable(user) &&
        fieldShiftAssignments
          .filter((assignment) => assignment.dispatch_ids.length === 0)
          .some((assignment) => assignment.user_id === user.id),
    );
  }, [searching, users, isSelectable, fieldShiftAssignments]);

  /**
   * Get movers who are not assigned to any shifts
   */
  const moversNotAssignedShifts = useMemo((): User[] => {
    const userList = searching ?? users;
    return userList.filter(
      (user) => isSelectable(user) && !fieldShiftAssignments.some((assignment) => assignment.user_id === user.id),
    );
  }, [searching, users, isSelectable, fieldShiftAssignments]);

  /**
   * Get movers in other regions
   */
  const moversInOtherRegions = useMemo((): User[] => {
    const userList = searching ?? users;
    return userList.filter((user) => !isSelectable(user) && isMover(user));
  }, [searching, users, isSelectable, isMover]);

  /**
   * Get non-movers
   */
  const nonMovers = useMemo((): User[] => {
    const userList = searching ?? users;
    return userList.filter((user) => !isMover(user));
  }, [searching, users, isMover]);

  /**
   * Build sections for the panel
   */
  const sections = useMemo((): Section[] => {
    return [
      { name: 'Assigned Shifts (Not Dispatched)', style: 'full', users: moversAssignedShifts },
      { name: 'Assigned Shifts (Dispatched)', style: 'warning', users: moversAssignedDispatches },
      { name: 'Not Assigned Shifts', style: 'none', users: moversNotAssignedShifts },
      { name: 'Other Regions', style: 'default', users: moversInOtherRegions },
      { name: 'Non-Movers', style: 'default', users: nonMovers },
    ];
  }, [moversAssignedShifts, moversAssignedDispatches, moversNotAssignedShifts, moversInOtherRegions, nonMovers]);

  /**
   * Reset search state
   */
  const reset = useCallback(() => {
    setAutocomplete(null);
    setSearching(null);
  }, []);

  /**
   * Handle search input change
   * @param text - The search text
   */
  const handleSearch = useCallback(
    (text: string) => {
      setAutocomplete(text);
      if (text.length > 0) {
        const escapedText = escapeRegExp(text);
        const regex = new RegExp(escapedText, 'i');
        setSearching(users.filter((user) => regex.test(user.name)));
      } else {
        reset();
      }
    },
    [users, reset],
  );

  /**
   * Build a new Dispatch object with an updated assignments array.
   * Produces a shallow copy so React and future non-Angular consumers
   * detect the change via reference inequality.
   * @param newAssignments - The replacement assignments array
   * @returns A new Dispatch with the updated assignments
   */
  const updatedDispatch = useCallback(
    (newAssignments: Assignment[]): Dispatch => {
      return { ...dispatch, assignments: newAssignments };
    },
    [dispatch],
  );

  /**
   * Clear selection and search UI state without emitting a dispatch update.
   * Handlers that modify assignments call this alongside onDispatchUpdated.
   */
  const clearSelection = useCallback(() => {
    setSelection(null);
    reset();
  }, [reset]);

  /**
   * Handle deselection — close the panel, discard incomplete assignments
   */
  const handleDeselect = useCallback(() => {
    let nextAssignments = dispatch.assignments;
    if (selection && !selection.user) {
      nextAssignments = dispatch.assignments.filter((a) => a !== selection);
    }
    clearSelection();
    onDispatchUpdated(updatedDispatch(nextAssignments));
  }, [selection, dispatch, clearSelection, onDispatchUpdated, updatedDispatch]);

  /**
   * Close the assignment panel when the Escape key is pressed anywhere in the document
   */
  useEffect(() => {
    if (!selection) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDeselect();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selection, handleDeselect]);

  /**
   * Handle reassignment — replace the user on an existing assignment
   * @param assignment - The assignment to update
   * @param user - The new user to assign
   */
  const handleReassign = useCallback(
    (assignment: Assignment, user: User) => {
      const nextAssignments = dispatch.assignments.map((a) =>
        a === assignment && a.user !== user ? { ...a, user, id: null } : a,
      );
      clearSelection();
      onDispatchUpdated(updatedDispatch(nextAssignments));
    },
    [dispatch, clearSelection, onDispatchUpdated, updatedDispatch],
  );

  /**
   * Auto-submit on enter (select first available mover)
   * @param text - The current search text
   */
  const handleAutosubmit = useCallback(
    (text: string) => {
      if (text.length > 0 && selection) {
        const user = head(moversAssignedShifts) ?? head(moversNotAssignedShifts);
        if (user) {
          handleReassign(selection, user);
        }
      }
    },
    [selection, moversAssignedShifts, moversNotAssignedShifts, handleReassign],
  );

  /**
   * Handle selection of an assignment
   * @param assignment - The assignment to select
   */
  const handleSelect = useCallback(
    (assignment: Assignment) => {
      if (!editing) return;
      setSelection(assignment);
      if (assignment.user) {
        setAutocomplete(assignment.user.name);
        handleSearch(assignment.user.name);
      }
    },
    [editing, handleSearch],
  );

  /**
   * Check if assignment is selected
   * @param assignment - The assignment to check
   * @returns True if the assignment is currently selected
   */
  const isSelected = useCallback(
    (assignment: Assignment): boolean => {
      return selection === assignment;
    },
    [selection],
  );

  /**
   * Handle removal of an assignment
   * @param assignment - The assignment to remove
   */
  const handleRemove = useCallback(
    (assignment: Assignment) => {
      const nextAssignments = assignment.id
        ? dispatch.assignments.map((a) => (a === assignment ? { ...a, _destroy: true } : a))
        : dispatch.assignments.filter((a) => a !== assignment);
      clearSelection();
      onDispatchUpdated(updatedDispatch(nextAssignments));
    },
    [dispatch, clearSelection, onDispatchUpdated, updatedDispatch],
  );

  /**
   * Handle insertion of a new assignment
   */
  const handleInsert = useCallback(() => {
    const hasLead = find(dispatch.assignments, (a) => !a._destroy && a.role?.toUpperCase() === Assignment__Role.Lead);
    const role = hasLead ? 'member' : 'lead';
    const assignment = { role };
    const next = updatedDispatch([...dispatch.assignments, assignment]);
    onDispatchUpdated(next);
    // Select the newly created assignment so the panel opens for editing
    setSelection(assignment);
  }, [dispatch, onDispatchUpdated, updatedDispatch]);

  /**
   * Handle showing hours worked tooltip
   * @param assignment - The assignment to show tooltip for
   */
  const handleShowHoursWorked = useCallback((assignment: Assignment) => {
    setActiveAssignment(assignment);
  }, []);

  /**
   * Handle hiding hours worked tooltip
   */
  const handleHideHoursWorked = useCallback(() => {
    setActiveAssignment(null);
  }, []);

  /**
   * Handle role change — produce a new assignments array with the role replaced
   * @param assignment - The assignment to update
   * @param newRole - The new role to assign
   */
  const handleRoleChange = useCallback(
    (assignment: Assignment, newRole: string) => {
      const updated = { ...assignment, role: newRole };
      const nextAssignments = dispatch.assignments.map((a) => (a === assignment ? updated : a));
      onDispatchUpdated(updatedDispatch(nextAssignments));
      setSelection(updated);
    },
    [dispatch, onDispatchUpdated, updatedDispatch],
  );

  /**
   * Handle key down for autosubmit
   * @param e - The keyboard event
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && autocomplete) {
        e.preventDefault();
        handleAutosubmit(autocomplete);
      }
    },
    [autocomplete, handleAutosubmit],
  );

  return (
    <div className="dispatcher-assignments">
      {dispatch.assignments.map(
        (assignment, index) =>
          !assignment._destroy && (
            <div
              key={assignment.id ?? `new-${index}`}
              className={`dispatcher-assignment${assignment.role ? ` dispatcher-assignment-${assignment.role.toLowerCase()}` : ''}`}
            >
              <div
                className="dispatcher-assignment-brick"
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(assignment)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(assignment);
                  }
                }}
                onMouseEnter={() => handleShowHoursWorked(assignment)}
                onMouseLeave={handleHideHoursWorked}
                onFocus={() => handleShowHoursWorked(assignment)}
                onBlur={handleHideHoursWorked}
                aria-label={`Assignment: ${assignment.user?.name ?? 'Unassigned'}, Role: ${titleize(assignment.role ?? '')}`}
              >
                <div className="dispatcher-assignment-text">{getInitials(assignment.user?.name)}</div>
                {assignment.role?.toUpperCase() === Assignment__Role.Lead && (
                  <div className="dispatcher-assignment-badge" aria-label="Lead">
                    ⭐️
                  </div>
                )}
                {assignment.role?.toUpperCase() === Assignment__Role.Driver && (
                  <div className="dispatcher-assignment-badge" aria-label="Driver">
                    🚛
                  </div>
                )}
                {activeAssignment === assignment && (
                  <div className="dispatcher-assignment-streak" role="tooltip">
                    <div>{assignment.user?.name}</div>
                  </div>
                )}
              </div>
              {isSelected(assignment) && (
                <div className="dispatcher-panel" role="dialog" aria-label="Assignment editor">
                  <div>
                    <div className="dispatcher-panel-banner">
                      <div className="form-inline">
                        {!editing && (
                          <>
                            <strong>{assignment.user?.name}</strong>
                            <span>({titleize(assignment.role ?? '')})</span>
                          </>
                        )}
                        {editing && (
                          <>
                            <input
                              className="form-control"
                              placeholder="Search movers"
                              autoFocus
                              value={autocomplete ?? ''}
                              onChange={(e) => handleSearch(e.target.value)}
                              onKeyDown={handleKeyDown}
                              aria-label="Search movers"
                            />
                            <select
                              className="form-control form-control-inline"
                              value={assignment.role ?? ''}
                              onChange={(e) => handleRoleChange(assignment, e.target.value)}
                              aria-label="Select role"
                            >
                              <option value="lead">Lead</option>
                              <option value="driver">Driver</option>
                              <option value="member">Member</option>
                              <option value="trainee">Trainee</option>
                            </select>
                          </>
                        )}
                      </div>
                    </div>
                    {editing && (
                      <div className="dispatcher-panel-content" role="listbox" aria-label="Available movers">
                        {sections.map((section) => (
                          <div key={section.name}>
                            {section.users.length > 0 && (
                              <div
                                className={`dispatcher-panel-section availability-${section.style}`}
                                role="group"
                                aria-label={section.name}
                              >
                                {section.name}
                              </div>
                            )}
                            {section.users.map((user) => (
                              <div
                                key={user.id}
                                className="dispatcher-panel-option"
                                role="option"
                                aria-selected={assignment.user?.id === user.id}
                                tabIndex={0}
                                onClick={(e) => {
                                  handleReassign(assignment, user);
                                  e.stopPropagation();
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleReassign(assignment, user);
                                    e.stopPropagation();
                                  }
                                }}
                                aria-label={`Select ${user.name}${user.driver_approved ? ', Driver approved' : ''}`}
                              >
                                {getHighlightedParts(user.name, autocomplete).map((part, partIndex) =>
                                  part.highlight ? (
                                    <strong key={partIndex}>{part.text}</strong>
                                  ) : (
                                    <span key={partIndex}>{part.text}</span>
                                  ),
                                )}
                                {user.driver_approved && <span className="pull-right badge badge-pill blue">D</span>}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                    {editing && (
                      <div className="dispatcher-panel-actions">
                        <button
                          type="button"
                          className="btn btn-danger btn-fill btn-sm"
                          onClick={(e) => {
                            handleRemove(assignment);
                            e.stopPropagation();
                          }}
                          aria-label="Remove assignment"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ),
      )}
      {editing && (
        <div className="dispatcher-assignment" onClick={handleInsert}>
          <a
            href="#"
            className="dispatcher-assignment-brick dispatcher-assignment-plus"
            onClick={(e) => e.preventDefault()}
            aria-label="Add new assignment"
          >
            <div className="dispatcher-assignment-text">+</div>
          </a>
        </div>
      )}
      {selection && <div className="dispatcher-overlay" role="presentation" onClick={handleDeselect} />}
    </div>
  );
};
